import { z } from 'zod';
import { baseListQuerySchema, sortOrderSchema, SortOrder } from './helpers';
import { knownTotalParamsSchema, knownTotalQuerySchema } from './known-total';
import { queryListFilterSchema } from './extensions';
import { PLATFORM_REGISTRY } from './platform-registry';
import { ColumnDataTypes, DATE_RANGE_OPERATORS, FilterOperators, FiltersSchema, createTableQuerySchema, } from './table';
// Re-export the shared filter type so plugins consumers don't need to reach
// into the extensions contract.
export { queryListFilterSchema };
/**
 * Plugin source — which discovery root produced the plugin.
 *  - `claude_code`: ~/.claude/plugins (via installed_plugins.json + /data fallback)
 *  - `claude_desktop_rpm`: ~/Library/Application Support/Claude/local-agent-mode-sessions/rpm/*
 *  - `gemini_cli`: ~/.gemini/extensions/<name>/gemini-extension.json (KIR-3178)
 *  - `codex`: ~/.codex MCP servers / config (KIR-3334)
 *  - `github_copilot_cli`: ~/.copilot installed plugins (config.json installedPlugins[] + agents/skills)
 */
export const pluginSourceSchema = z.enum([
    'claude_code',
    'claude_desktop_rpm',
    'gemini_cli',
    'codex',
    'github_copilot_cli',
]);
/** Map each `PluginSource` to its `PLATFORM_REGISTRY` key (not 1:1). */
export const PLUGIN_SOURCE_TO_PLATFORM = {
    claude_code: 'claude',
    claude_desktop_rpm: 'claude_desktop',
    gemini_cli: 'gemini_cli',
    codex: 'codex',
    github_copilot_cli: 'github_copilot_cli',
};
/** Resolve a `PluginSource` to its registry display label. */
export function getPluginSourceLabel(source) {
    return PLATFORM_REGISTRY[PLUGIN_SOURCE_TO_PLATFORM[source]].displayName;
}
export const pluginIdSchema = z.string().min(1);
/**
 * PluginFeatures — the structured contents of a plugin: every entity it
 * declares (commands, agents, skills, mcp servers, hooks, monitors, themes)
 * plus a categorized list of bundled files. Captured by the agent at scan time
 * and shown in the org-admin Plugins tab when a version row is expanded.
 */
export const pluginCommandSchema = z.object({
    name: z.string(),
    description: z.string().optional(),
    path: z.string().optional(),
});
export const pluginAgentSchema = z.object({
    name: z.string(),
    description: z.string().optional(),
    model: z.string().optional(),
    path: z.string().optional(),
});
export const pluginSkillSchema = z.object({
    name: z.string(),
    description: z.string().optional(),
    path: z.string().optional(),
});
export const pluginMcpServerSchema = z.object({
    name: z.string(),
    transport: z.enum(['stdio', 'http', 'sse']),
    commandRef: z.string().optional(),
});
export const pluginHookSchema = z.object({
    event: z.string(),
    matcher: z.string().optional(),
    handlerTypes: z.array(z.string()).optional(),
    count: z.number().int().min(0),
    commandRefs: z.array(z.string()).optional(),
});
export const pluginMonitorSchema = z.object({
    name: z.string(),
    description: z.string().optional(),
    commandRef: z.string().optional(),
});
export const pluginThemeSchema = z.object({
    name: z.string(),
    path: z.string().optional(),
});
export const pluginExecutableFileSchema = z.object({
    path: z.string(),
    kind: z.enum([
        'shell',
        'python',
        'node',
        'ruby',
        'perl',
        'powershell',
        'batch',
        'binary',
        'other',
    ]),
    executableBit: z.boolean().optional(),
    referencedBy: z.array(z.string()).optional(),
});
export const pluginInstructionFileSchema = z.object({
    path: z.string(),
    title: z.string().optional(),
    referencedBy: z.array(z.string()).optional(),
});
export const pluginBundledFilesSchema = z.object({
    executables: z.array(pluginExecutableFileSchema).optional(),
    instructions: z.array(pluginInstructionFileSchema).optional(),
    truncated: z.boolean().optional(),
});
export const pluginFeaturesSchema = z.object({
    commands: z.array(pluginCommandSchema).optional(),
    agents: z.array(pluginAgentSchema).optional(),
    skills: z.array(pluginSkillSchema).optional(),
    mcpServers: z.array(pluginMcpServerSchema).optional(),
    hooks: z.array(pluginHookSchema).optional(),
    monitors: z.array(pluginMonitorSchema).optional(),
    themes: z.array(pluginThemeSchema).optional(),
    files: pluginBundledFilesSchema.optional(),
});
export const pluginVersionSchema = z.object({
    version: z.string().nullable(),
    userCount: z.number().int().min(0),
    firstSeenAt: z.string().datetime(),
    features: pluginFeaturesSchema.nullish(),
});
/**
 * `pluginListItemSchema` — a row in the aggregated inventory list.
 *
 * Identity is `(pluginName, marketplace)`, NOT pluginName alone: two unrelated
 * public plugins from different marketplaces can share a pluginName, so we
 * keep them as separate rows. NULL marketplace is its own bucket (covers the
 * data-folder fallback and claude_desktop_rpm without an RPM manifest).
 *
 * Consumers building React keys / stable cache keys should use the composite
 * `${pluginName}@${marketplace ?? ''}` form.
 */
export const pluginListItemSchema = z.object({
    pluginName: z.string(),
    marketplace: z.string().nullable(),
    displayName: z.string().nullable(),
    versions: z.array(pluginVersionSchema),
    sources: z.array(pluginSourceSchema),
    userCount: z.number().int().min(0),
    firstSeenAt: z.string().datetime(),
    lastSeenAt: z.string().datetime(),
});
export const pluginListResponseSchema = z.object({
    plugins: z.array(pluginListItemSchema),
    pagination: z.object({
        page: z.number().int().min(1),
        limit: z.number().int().min(1).max(100),
        total: z.number().int().min(0),
        hasMore: z.boolean(),
    }),
});
/**
 * Plugin inventory request — sent by the agent on each rescan.
 */
export const pluginInventoryItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    marketplace: z.string().nullable().optional(),
    version: z.string().nullable().optional(),
    displayName: z.string().optional(),
    installPath: z.string().nullable().optional(),
    installScope: z.string().nullable().optional(),
    gitCommitSha: z.string().nullable().optional(),
    features: pluginFeaturesSchema.nullish(),
});
export const pluginInventoryRequestSchema = z.object({
    plugins: z.array(pluginInventoryItemSchema),
    source: pluginSourceSchema,
});
export const pluginInventoryResponseSchema = z.object({
    success: z.boolean(),
});
export const pluginInventoryDeleteRequestSchema = z.object({
    source: pluginSourceSchema,
    version: z.string().nullable().optional(),
});
export const pluginInventoryDeleteResponseSchema = pluginInventoryResponseSchema;
export const pluginSortFieldSchema = z.enum([
    'name',
    'marketplace',
    'source',
    'firstSeenAt',
    'lastSeenAt',
    'userCount',
]);
export const pluginSortOrderSchema = sortOrderSchema;
// Canonical DataTable filter contract (org + personal paths, table.ts
// `FilterValue`).
/** Filterable fields for the org Plugins DataTable (ENUM, except First Seen = DATE). */
export const PLUGIN_FILTERABLE_FIELDS = {
    NAME: 'name',
    SOURCE: 'source',
    MARKETPLACE: 'marketplace',
    USERS: 'users',
    FIRST_SEEN_AT: 'firstSeenAt',
    LAST_SEEN_AT: 'lastSeenAt',
};
/**
 * Column data types for the org Plugins DataTable. Drives which operators are
 * valid via `createTableQuerySchema`. Name is ENUM (dynamic options),
 * deliberately NOT STRING+CONTAINS like MCP's Name.
 */
export const PLUGIN_COLUMN_DATA_TYPES = {
    [PLUGIN_FILTERABLE_FIELDS.NAME]: ColumnDataTypes.ENUM,
    [PLUGIN_FILTERABLE_FIELDS.SOURCE]: ColumnDataTypes.ENUM,
    [PLUGIN_FILTERABLE_FIELDS.MARKETPLACE]: ColumnDataTypes.ENUM,
    [PLUGIN_FILTERABLE_FIELDS.USERS]: ColumnDataTypes.ENUM,
    [PLUGIN_FILTERABLE_FIELDS.FIRST_SEEN_AT]: ColumnDataTypes.DATE,
    [PLUGIN_FILTERABLE_FIELDS.LAST_SEEN_AT]: ColumnDataTypes.DATE,
};
// Per-column operators: ENUM → INCLUDES/EXCLUDES, DATE → range, every column
// also value-less (no-op except nullable Marketplace). Shared with personal.
export const PLUGIN_OPERATOR_OVERRIDES = {
    [PLUGIN_FILTERABLE_FIELDS.NAME]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [PLUGIN_FILTERABLE_FIELDS.SOURCE]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [PLUGIN_FILTERABLE_FIELDS.MARKETPLACE]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [PLUGIN_FILTERABLE_FIELDS.USERS]: [
        FilterOperators.INCLUDES,
        FilterOperators.EXCLUDES,
        FilterOperators.IS_EMPTY,
        FilterOperators.IS_NOT_EMPTY,
    ],
    [PLUGIN_FILTERABLE_FIELDS.FIRST_SEEN_AT]: DATE_RANGE_OPERATORS,
    [PLUGIN_FILTERABLE_FIELDS.LAST_SEEN_AT]: DATE_RANGE_OPERATORS,
};
/**
 * Canonical org Plugins list query schema. Handles bracket-notation filter
 * params (`field[operator]=v1,v2`) from Fastify OR a pre-parsed `filters`
 * array. Default sort is userCount desc (org admin surfaces the Users column).
 */
const pluginTableQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: pluginSortFieldSchema.default('userCount'),
    sortOrder: sortOrderSchema.default(SortOrder.Desc),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
});
export const pluginTableQuerySchema = createTableQuerySchema(pluginTableQueryBaseSchema, PLUGIN_COLUMN_DATA_TYPES, PLUGIN_OPERATOR_OVERRIDES);
export const listOrgPluginsParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    sortBy: pluginSortFieldSchema.optional(),
    sortOrder: pluginSortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalParamsSchema,
});
// Personal (account-scope) canonical Plugins contract — the org defs minus
// `users` (a single-user account has no cross-user filter), default sort name
// asc. Derived from the org `PLUGIN_*` consts so the two stay in lockstep.
const { users: _orgUsersDataType, ...PERSONAL_PLUGIN_COLUMN_DATA_TYPES } = PLUGIN_COLUMN_DATA_TYPES;
const { users: _orgUsersOverride, ...PERSONAL_PLUGIN_OPERATOR_OVERRIDES } = PLUGIN_OPERATOR_OVERRIDES;
const personalPluginTableQueryBaseSchema = baseListQuerySchema.extend({
    sortBy: pluginSortFieldSchema.default('name'),
    sortOrder: sortOrderSchema.default(SortOrder.Asc),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalQuerySchema,
});
/**
 * Canonical personal Plugins list query schema. Same bracket-notation handling
 * as the org schema, but `users` is not a filterable field and the default sort
 * is name asc (not userCount desc).
 */
export const personalPluginTableQuerySchema = createTableQuerySchema(personalPluginTableQueryBaseSchema, PERSONAL_PLUGIN_COLUMN_DATA_TYPES, PERSONAL_PLUGIN_OPERATOR_OVERRIDES);
export const listPersonalPluginsParamsSchema = z.object({
    page: z.number().int().min(1).optional(),
    limit: z.number().int().min(1).max(100).optional(),
    search: z.string().optional(),
    sortBy: pluginSortFieldSchema.optional(),
    sortOrder: pluginSortOrderSchema.optional(),
    filters: FiltersSchema.optional(),
    knownTotal: knownTotalParamsSchema,
});
// Filter-options contract — dynamic ENUM options for Name / Marketplace / Users.
export const pluginFilterOptionFieldSchema = z.enum([
    PLUGIN_FILTERABLE_FIELDS.NAME,
    PLUGIN_FILTERABLE_FIELDS.MARKETPLACE,
    PLUGIN_FILTERABLE_FIELDS.USERS,
]);
export const pluginFilterOptionsQuerySchema = z.object({
    field: pluginFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
// Personal filter-options — Name / Marketplace only (no `users`, an org concept).
export const personalPluginFilterOptionFieldSchema = z.enum([
    PLUGIN_FILTERABLE_FIELDS.NAME,
    PLUGIN_FILTERABLE_FIELDS.MARKETPLACE,
]);
export const personalPluginFilterOptionsQuerySchema = z.object({
    field: personalPluginFilterOptionFieldSchema,
    search: z.string().optional(),
    limit: z.coerce.number().int().positive().max(100).optional(),
    offset: z.coerce.number().int().min(0).optional(),
});
/**
 * Rich filter option item with value, label, and optional metadata (used for
 * Users where the value is a UUID and the label/metadata carry name + email).
 */
export const pluginFilterOptionItemSchema = z.object({
    value: z.string(),
    label: z.string().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const pluginFilterOptionsResponseSchema = z.object({
    field: pluginFilterOptionFieldSchema,
    options: z.union([
        z.array(z.string()),
        z.array(pluginFilterOptionItemSchema),
    ]),
    pagination: z.object({
        total: z.number().int().min(0),
        limit: z.number().int().min(0),
        offset: z.number().int().min(0),
        hasMore: z.boolean(),
        nextOffset: z.number().int().min(0).nullable().optional(),
    }),
});
export const pluginUserSchema = z.object({
    userId: z.string().uuid(),
    email: z.string().nullable(),
    name: z.string().nullable(),
    sources: z.array(pluginSourceSchema),
    version: z.string().nullable(),
    lastSeenAt: z.string().datetime(),
});
export const pluginUsersResponseSchema = z.object({
    pluginName: z.string(),
    marketplace: z.string().nullable(),
    users: z.array(pluginUserSchema),
});
//# sourceMappingURL=plugins.js.map